import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Grid,
    Button,
    Divider,
    Backdrop,
    CircularProgress
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { 
    GetOptionalHolidayCalendarAction,
    GetEmployeeOptionalHolidayCalendarAction,
    UpsertEmployeeOptionalHolidayCalendarAction,
    DeleteEmployeeOptionalHolidayCalendarAction
} from "../../../../../../store/Leave/OptionalHolidayCalendar/Action";
import SelectableSearch from "../../../../Base/views/SelectableSearch";
import { getEmployeeId } from "../../../../Base/helper/baseFunctions";

const ConfigureOptionalLeavePopup = ({ setOpenPopup }) => {
    const dispatch = useDispatch();

    // Fetch optional holiday calendar data from Redux state
    const { 
        optionalHolidayData, 
        isLoadingOptionalHoliday,
        employeeOptionalHolidayData,
        isLoadingEmployeeOptionalHoliday
    } = useSelector((state) => state.LeaveReducer?.OptionalHolidayCalendarReducer)

    // Component State
    const [selectedLeaves, setSelectedLeaves] = useState([]);
    const [year] = useState(new Date().getFullYear());
    const [errors, setErrors] = useState({});


    useEffect(() => {
        dispatch(GetOptionalHolidayCalendarAction({ year: year }));
        dispatch(GetEmployeeOptionalHolidayCalendarAction({ year: year, employee_fk: getEmployeeId() }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year]);

    // Load saved leaves from Redux state when optionalHolidayData or mappings change
    useEffect(() => {
        if (employeeOptionalHolidayData?.length > 0) {
            
            const selfData = employeeOptionalHolidayData?.filter(el => +el?.employee_id === getEmployeeId())

            setSelectedLeaves(selfData);
        } else {
            setSelectedLeaves([]);
        }
    }, [employeeOptionalHolidayData]);

    // Handle form validation
    const validate = () => {
        let temp = {};
        temp.selectedLeaves = selectedLeaves.length > 0 ? "" : "Please select at least one optional holiday.";
        setErrors(temp);
        return Object.values(temp).every((x) => x === "");
    };

    // Save handler: Persists selection in the database
    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            // Get currently saved list belonging to this employee
            const oldLeaves = employeeOptionalHolidayData?.filter(el => +el?.employee_id === getEmployeeId()) || [];
            const newLeaves = selectedLeaves || [];
            // 1. Identify deleted mappings
            const deleted = oldLeaves.filter(oldItem => 
                !newLeaves.some(newItem => newItem.optional_holidaycalendar_pk === oldItem.optional_holidaycalendar_pk)
            );
            // 2. Identify added mappings
            const added = newLeaves.filter(newItem => 
                !oldLeaves.some(oldItem => oldItem.optional_holidaycalendar_pk === newItem.optional_holidaycalendar_pk)
            );
            if (added?.length > 0) {
                added?.forEach((item,index) => {
                    dispatch(UpsertEmployeeOptionalHolidayCalendarAction({
                        employee_fk: getEmployeeId(),
                        optional_holidaycalendar_pk: item.optional_holidaycalendar_pk,
                        reason_description: "",
                        year: new Date().getFullYear(),
                        toast: index === 0,
                    },
                    (result,error) => {
                    if (!error && result?.data?.statuscode === 200) {
                        if (deleted.length > 0) {
                            const idsString = `{${deleted.map(item => item?.employee_optional_holidaycalendar_mapping_pk).join(',')}}`;
                            dispatch(DeleteEmployeeOptionalHolidayCalendarAction({
                                year: new Date().getFullYear(),
                                employee_optional_holidaycalendar_ids: idsString,
                                toast: "updating"
                            }));
                        }
                    } 
                    }
                ));
                });
            } else {
                if (deleted.length > 0) {
                    const idsString = `{${deleted.map(item => item?.employee_optional_holidaycalendar_mapping_pk).join(',')}}`;
                    dispatch(DeleteEmployeeOptionalHolidayCalendarAction({
                        year: new Date().getFullYear(),
                        employee_optional_holidaycalendar_ids: idsString,
                        toast: "updating"
                    }));
                }
            }
            setTimeout(()=> {
                setOpenPopup(false);
            },2000)
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ p: 1 }} position={"relative"}>
            <Backdrop
                sx={{
                    zIndex: 100,
                    backgroundColor: "rgba(255, 255, 255, 0.5)",
                    position: "absolute", 
                    borderRadius: "inherit", 
                }}  
                open={isLoadingEmployeeOptionalHoliday} 
            >
                <CircularProgress  />
            </Backdrop>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <SelectableSearch
                        multiple={true}
                        id="optional-leaves-selector"
                        name="selectedOptionalLeaves"
                        label="Select Optional Holidays"
                        placeholder={selectedLeaves.length === 0 ? "Search and select optional holiday..." : ""}
                        value={selectedLeaves}
                        onChangeEvent={(name, value) => {
                            setSelectedLeaves(value || []);
                            if (value && value.length > 0) {
                                setErrors({ ...errors, selectedLeaves: "" });
                            }
                        }}
                        loading={() => {}}
                        fieldLabel="name"
                        isOptionEqualToValue={(option, value) => option.optional_holidaycalendar_pk === value.optional_holidaycalendar_pk}
                        data={{
                            values: optionalHolidayData || [],
                            load: isLoadingOptionalHoliday
                        }}
                        required={true}
                        {...(errors.selectedLeaves && {
                            error: true,
                            helperText: errors.selectedLeaves,
                        })}
                    />
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Actions */}
            <Box display="flex" justifyContent="flex-end" gap="12px">
                <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    className="mpl-primary-btn"
                    sx={{
                        borderRadius: "8px",
                        fontWeight: 700,
                    }}
                >
                    Update
                </Button>
            </Box>
        </Box>
    );
};

export default ConfigureOptionalLeavePopup;
